import React, { useEffect } from 'react';
import { DownloadOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { Table, Row, Col, Button, Select, Form, DatePicker, Tooltip, Tag, List } from 'antd';
import { intialPaginationState, showingPageTotalRange, tailFormItemLayout, formItemLayout } from '../../common_functions';
import { STATUS } from '../../config';
import moment from 'moment';
import useReportHistory from './hooks/useReportHistory';
import { getSortLabel } from './bibliography/bibliographyUtils';

const { RangePicker } = DatePicker;

const BIBLIO_DEFAULT_FILTERS = { report_type: { _in: [5, 6, 7] } };

const BIBLIO_TYPE_LABELS = {
  5: 'Plate Inventory',
  6: 'Published Images',
  7: 'Books List',
};

const STATUS_MAP = {
  draft: { color: 'orange', label: 'Pending' },
  in_progress: { color: 'blue', label: 'In Progress' },
  published: { color: 'green', label: 'Completed' },
};

const safeParseJson = val => {
  try {
    return JSON.parse(val);
  } catch {
    return {};
  }
};

const safeParseArray = val => {
  if (Array.isArray(val)) return val;
  try {
    const parsed = JSON.parse(val);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const renderList = items => (
  <List
    dataSource={items}
    renderItem={item => (
      <List.Item style={{ display: 'list-item', listStyleType: 'disc', marginLeft: '20px', borderBottom: 'none' }}>
        {item}
      </List.Item>
    )}
  />
);

const BibliographyReportHistory = props => {
  const { formRef, pagination, loading, history, setHistory, getHistory, handleTableChange, handleDownLoadFile, resetFilters } =
    useReportHistory(props, BIBLIO_DEFAULT_FILTERS);

  useEffect(() => {
    (async () => {
    await getHistory(intialPaginationState);
  })();
  }, []);

  const columns = [
    {
      title: 'Generated Date',
      dataIndex: 'date_created',
      key: 'date_created',
      render: date => moment(date).format('MM/DD/YYYY, h:mm:ss A'),
    },
    {
      title: 'Report Type',
      dataIndex: 'report_type',
      key: 'report_type',
      render: type => (
        <Tag color="geekblue" style={{ fontWeight: 500 }}>
          {BIBLIO_TYPE_LABELS[Number(type)] || type}
        </Tag>
      ),
    },
    {
      title: 'Book / Category',
      dataIndex: 'collections',
      key: 'collections',
      render: (collectionsVal, record) => {
        const type = Number(record.report_type);
        const names = safeParseArray(collectionsVal);
        if (!names.length) return <span style={{ color: '#bbb' }}>—</span>;
        if (type === 6 || type === 7) {
          return <Tag color="purple">{names[0]}</Tag>;
        }
        return renderList(names);
      },
      width: 240,
    },
    {
      title: 'Details',
      dataIndex: 'object_titles',
      key: 'object_titles',
      render: (object_titles, record) => {
        const type = Number(record.report_type);
        const parsed = safeParseJson(object_titles);
        if (type === 7) {
          if (Array.isArray(parsed?.book_titles) && parsed.book_titles.length) {
            return renderList(parsed.book_titles);
          }
          return <span style={{ color: '#bbb' }}>—</span>;
        }
        if (type === 6) {
          const count = Array.isArray(parsed?.selected_ids)
            ? parsed.selected_ids.length
            : record.number_of_records_in_pdf;
          return <Tag color="cyan">{count ?? '—'} image(s) selected</Tag>;
        }
        if (type === 5) {
          const sortLabel = getSortLabel(parsed?.sort_by) || 'Default order';
          return <Tag color="blue">Sort: {sortLabel}</Tag>;
        }
        return <span style={{ color: '#bbb' }}>—</span>;
      },
      width: 300,
    },
    {
      title: 'No of Records',
      dataIndex: 'number_of_records_in_pdf',
      key: 'number_of_records_in_pdf',
      align: 'center',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: status => {
        const { color, label } = STATUS_MAP[status] || { color: 'red', label: 'Failed' };
        return <Tag color={color}>{label}</Tag>;
      },
    },
    {
      title: 'Actions',
      key: 'action',
      fixed: 'right',
      render: (_, record) =>
        record?.report?.id ? (
          <div className="actionbtngroup">
            {(props.roleType === 'admin' || props?.tabs_permissions?.settings_report_history?.view) && (
              <Tooltip placement="top" title="Download">
                <button className="edit-btn" onClick={() => handleDownLoadFile(record.report.id, record.report.filename_download)}>
                  <DownloadOutlined />
                </button>
              </Tooltip>
            )}
            {props.deleteRecord(
              props,
              record.id,
              'report_history',
              'this record permanently?',
              history,
              setHistory,
              props?.tabs_permissions?.settings_report_history?.delete
            )}
          </div>
        ) : null,
    },
  ];

  const onFinish = async values => {
    const filters = {};
    let condition = {};
    if (values?.date_created) {
      condition = {
        _and: [
          { date_created: { _gte: values.date_created[0].format('YYYY-MM-DD') + 'T00:00:00' } },
          { date_created: { _lte: values.date_created[1].format('YYYY-MM-DD') + 'T23:59:59' } },
        ],
      };
    }
    if (values?.status) {
      filters['status'] = { _eq: values.status };
    }
    if (values?.report_type) {
      filters['report_type'] = { _eq: values.report_type };
    }
    await getHistory(intialPaginationState, { ...filters, ...condition });
  };

  return (
    <div className={loading ? 'disabledComponent' : ''} style={{ paddingTop: 20 }}>
      <Form ref={formRef} layout="vertical" onFinish={onFinish} {...formItemLayout} initialValues={{ layout: tailFormItemLayout }}>
        <Row gutter={24}>
          <Col xs={24} sm={12} md={8} lg={8}>
            <Form.Item name="date_created" label="Generated Date">
              <RangePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={8}>
            <Form.Item name="status" label="Status">
              <Select
                placeholder="Select status"
                optionFilterProp="children"
                showSearch
                allowClear
                filterOption={(input, option) => option.children.includes(input)}
              >
                {STATUS.map(col => (
                  <Select.Option key={col.key} value={col.key}>
                    {col.value}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={8}>
            <Form.Item name="report_type" label="Report Sub-type">
              <Select placeholder="Select sub-type" allowClear>
                {Object.entries(BIBLIO_TYPE_LABELS).map(([val, label]) => (
                  <Select.Option key={val} value={Number(val)}>
                    {label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={9} lg={9}>
            <Form.Item>
              <Button
                type="primary"
                size="small"
                htmlType="submit"
                style={{ marginTop: '28px', height: '42px' }}
                icon={<SearchOutlined />}
              >
                Search
              </Button>
              <Button
                style={{ marginLeft: '10px', height: '42px', marginTop: '28px' }}
                size="small"
                danger
                onClick={resetFilters}
                icon={<ReloadOutlined />}
              >
                Reset Filters
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <hr />
      <div className="artist-list">
        <Table
          rowKey="id"
          scroll={{ x: 'max-content' }}
          columns={columns}
          bordered
          dataSource={history}
          pagination={history.length > 0 ? { ...pagination, ...showingPageTotalRange } : false}
          loading={loading}
          onChange={handleTableChange}
        />
      </div>
    </div>
  );
};

export default BibliographyReportHistory;
